import {Customer} from '@hconnect/apiclient'
import {flagPathByCountryCode, useBreakPoints} from '@hconnect/uikit'
import {H3, Pill} from '@hm/fundamental'
import React from 'react'

import {useTranslation} from '../../../hooks/useTranslation'
import {GroupedRoleAssignment} from '../../../modules/ManageUsers.selectors'

type CollapsedRoleContentProps = {
  groupedRoleAssignment: GroupedRoleAssignment
  customers?: Customer[]
  countryId: string
  businessLine: string
  getProjectsSitesLabel: (siteIds: string[], projectIds: string[]) => string
  hasProjectSiteIdScopeInConfiguration: boolean
  hasContractIdScopeInConfiguration: boolean
  renderHeaderItems?: (items: React.ReactNode) => void
  renderTitle?: (title: React.ReactNode) => void
}

export const CollapsedRoleContent = ({
  groupedRoleAssignment,
  customers,
  countryId,
  businessLine,
  getProjectsSitesLabel,
  hasProjectSiteIdScopeInConfiguration,
  hasContractIdScopeInConfiguration,
  renderHeaderItems,
  renderTitle
}: CollapsedRoleContentProps) => {
  const {t} = useTranslation()
  const screenSizes = useBreakPoints()
  const isMobile = ['xs', 'sm'].includes(screenSizes)

  const {customersScopes} = groupedRoleAssignment.variantDataScopes

  const allPillTexts: {projects: string[]; contracts: string[]} = {
    projects: [],
    contracts: []
  }

  customersScopes.forEach((scope) => {
    if (scope.siteIds.length || scope.projectIds.length || hasProjectSiteIdScopeInConfiguration) {
      allPillTexts.projects.push(getProjectsSitesLabel(scope.siteIds, scope.projectIds))
    }

    if (scope.contractIds.length || hasContractIdScopeInConfiguration) {
      allPillTexts.contracts.push(
        t(
          scope.contractIds.length === 0
            ? 'roleAssignment.contractsPlaceholder_all'
            : 'roleAssignment.contractsPlaceholder',
          {contracts: scope.contractIds.join(', ')}
        )
      )
    }
  })

  const uniqueProject =
    Array.from(new Set(allPillTexts.projects)).length === 1
      ? Array.from(new Set(allPillTexts.projects))[0]
      : ''
  const uniqueContract =
    Array.from(new Set(allPillTexts.contracts)).length === 1
      ? Array.from(new Set(allPillTexts.contracts))[0]
      : ''

  const headerItems = (
    <div className="flex gap-2 items-center">
      {!!countryId && countryId !== '*' && countryId !== 'MOCK' && (
        <img src={flagPathByCountryCode(countryId)} alt={`${countryId}-flag`} />
      )}
      {businessLine && <Pill type="neutral" shape="square" text={businessLine} />}
      {!isMobile && (
        <>
          {uniqueProject && <Pill type="neutral" shape="square" text={uniqueProject} />}
          {uniqueContract && <Pill type="neutral" shape="square" text={uniqueContract} />}
        </>
      )}
    </div>
  )

  const titleContent = () => {
    if (customersScopes.length === 1 && customers) {
      return (
        <div>
          {customersScopes.map((scope) => {
            const customerData = customers.find(
              (customer) => customer.customerId === scope.customerId
            )
            if (!customerData) return null

            return (
              <div key={customerData.customerId} className="flex gap-2">
                <div className="self-stretch w-1px border-1px ml-2 border-foreground" />
                <H3 className="!font-normal">{customerData.customerName}</H3>
                <div className="self-stretch w-1px border-1px" />
                <H3 className="!font-normal">{customerData.customerNumber}</H3>
              </div>
            )
          })}
        </div>
      )
    }

    if (customersScopes.length > 1 && customers) {
      return (
        <div className="flex gap-2">
          <div className="self-stretch w-1px border-1px ml-2 border-foreground" />
          <H3 className="!font-normal">Multiple customers</H3>
        </div>
      )
    }

    return null
  }

  renderHeaderItems?.(headerItems)
  renderTitle?.(titleContent())

  return {
    headerItems,
    titleContent
  }
}
